import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL } from '../../lib/legal';

/*
 * The two legal documents, written in plain English around how WannaPublish actually works (14-day trial, Stripe
 * portal cancellation, referral programme, 30-day Trash, export / delete from Profile settings). Each document is a list
 * of sections { id, title, body } so the page can build a table of contents. Business details come from lib/legal.js.
 */

const L = LEGAL;
const Contact = () => <a href={`mailto:${L.contactEmail}`} className="text-primary underline underline-offset-2" data-testid="legal-contact-email">{L.contactEmail}</a>;
const Mail = ({ children }) => <a href={`mailto:${L.contactEmail}`} className="text-primary underline underline-offset-2">{children}</a>;
const To = ({ to, children }) => <Link to={to} className="text-primary underline underline-offset-2">{children}</Link>;

export const TERMS = {
  id: 'terms',
  title: 'Terms of Service',
  intro: `These terms are the agreement between you and ${L.operator} for using ${L.brand}. They are written to be read, not skimmed — the short version: it is a paid subscription with a free trial, you can cancel yourself at any time, your work stays yours, and we try hard to be fair.`,
  sections: [
    {
      id: 'who', title: '1. Who we are and what you are agreeing to',
      body: (
        <>
          <p>{L.brand} ("the service") is a desktop-publishing application that runs in your web browser. It is operated by <strong>{L.operator}</strong>{L.operatorAddress ? ` (${L.operatorAddress})` : ''} ("we", "us"). You can reach us at <Contact />.</p>
          <p>By creating an account or using the service you agree to these terms and to our <To to="/privacy">Privacy Policy</To>. If you do not agree, please do not use the service.</p>
          <p>You must be at least 16 years old (or the minimum age required in your country to agree to terms like these) to create an account.</p>
        </>
      ),
    },
    {
      id: 'account', title: '2. Your account',
      body: (
        <>
          <p>You can sign in with an email address and password or with a Google account. Keep your sign-in details private and tell us straight away at <Contact /> if you think someone else has used your account. You are responsible for everything that happens under your account.</p>
          <p>One person, one account. Creating extra accounts to claim additional free trials or referral rewards is not allowed and we may close such accounts without refund.</p>
        </>
      ),
    },
    {
      id: 'subscriptions', title: '3. Subscriptions, prices and the free trial',
      body: (
        <>
          <p>{L.brand} is a paid service. Two plans are offered, both priced in {L.currency}:</p>
          <ul>
            <li><strong>Monthly</strong> — {L.monthlyPrice} per month.</li>
            <li><strong>Annual</strong> — {L.annualPrice} per year.</li>
          </ul>
          <p><strong>Free trial.</strong> New accounts get a {L.trialDays}-day free trial. A payment card is required to start it, but it is <strong>not charged during the trial</strong>. When the trial ends your subscription starts automatically and your card is charged the plan price — unless you cancel before the trial ends, in which case nothing is charged. The trial is offered once per account.</p>
          <p><strong>Automatic renewal.</strong> Subscriptions renew automatically at the end of each billing period (monthly or yearly) at the then-current price until you cancel. You will receive an email receipt for each payment.</p>
          <p><strong>Price changes.</strong> If we change prices we will email you at least 30 days before the new price applies to you, so you can cancel first if you prefer.</p>
          <p><strong>Payments and taxes.</strong> Payments are processed by Stripe; we never see or store your full card number. Prices may exclude sales tax or VAT where it applies, in which case it is shown before you pay.</p>
          <p><strong>Discount codes.</strong> Promotional and referral discounts apply as described when they are offered, cannot be exchanged for cash and may be withdrawn if they are misused.</p>
        </>
      ),
    },
    {
      id: 'cancel', title: '4. Cancelling',
      body: (
        <>
          <p>You can cancel at any time, yourself, without contacting us: inside the app open <strong>Account ▸ Billing &amp; plan ▸ Manage in Stripe</strong> and choose <em>Cancel plan</em>. The same page lets you switch between monthly and annual, update your card and download invoices.</p>
          <p>When you cancel, your subscription stays active until the end of the period you have already paid for (or until the end of your trial), and then stops. You keep full access until that date. Your publications and files are not deleted when a subscription ends — you can still sign in, download everything from <strong>Profile settings</strong>, and resubscribe whenever you like.</p>
          <p><strong>Failed payments.</strong> If a renewal payment fails, Stripe retries the card for a while and we email you so you can update it. If the payment keeps failing the subscription ends and the editor closes until a plan is started again. Your work is kept.</p>
        </>
      ),
    },
    {
      id: 'refunds', title: '5. Refunds',
      body: (
        <>
          <p>Because you can try everything free for {L.trialDays} days and cancel at any time, payments are generally <strong>not refundable</strong> once a billing period has started, and we do not give partial refunds for unused time.</p>
          <p>That said, if something has gone wrong — for example you were charged after cancelling, you were charged twice, or the service was unusable for you because of a fault on our side — email <Contact /> within {L.refundWindowDays} days of the charge and we will put it right, including a refund where appropriate.</p>
          <p>Nothing in this section limits any statutory rights you have as a consumer. If you live in the EU or UK, you agree that the service begins immediately when your subscription (or trial) starts and you acknowledge that the 14-day right of withdrawal no longer applies once the service has been fully provided for that period — you can still cancel for the future at any time as described above.</p>
        </>
      ),
    },
    {
      id: 'referrals', title: '6. Refer-a-friend programme',
      body: (
        <>
          <p>Every account has a personal referral link and code (<strong>Account ▸ Refer a friend</strong>). When someone creates a new account through your link or enters your code before their first checkout:</p>
          <ul>
            <li><strong>Your friend</strong> gets their first month free on the monthly plan, or {'$5'} off their first year on the annual plan.</li>
            <li><strong>You</strong> get one free month (a {L.monthlyPrice} credit) if you are on the monthly plan, or a {'$5'} credit if you are on the annual plan. The reward is granted <strong>after your friend's first paid invoice</strong> (an invoice above {'$0'}) has been settled — not when they sign up or start a trial.</li>
          </ul>
          <p>Credits are applied automatically to your next invoice, have no cash value, cannot be transferred, and are forfeited if your subscription ends before they are used. Each new account can be referred once; you cannot refer yourself or accounts you control. We may withhold or reverse rewards obtained through fake accounts, misleading promotion or other abuse, and we may change or end the programme at any time (rewards already granted are honoured).</p>
        </>
      ),
    },
    {
      id: 'content', title: '7. Your content',
      body: (
        <>
          <p>Everything you create or upload — publications, images, fonts, templates — remains <strong>yours</strong>. You give us only the permission we need to run the service: to store, back up, convert, display and transmit your content so you can edit, export and share it. We do not use your content for advertising or to train anything.</p>
          <p>You are responsible for having the rights to what you upload (for example commercial licences for fonts and photos) and for how you use the output. Stock photos found through the built-in search come from Unsplash and Pexels and are subject to their licences.</p>
          <p><strong>Share links.</strong> When you create a share link, anyone with the link who signs in receives their own copy of that publication. You can turn a link off at any time, which stops new copies; copies already made stay with their recipients.</p>
          <p><strong>Legacy Publisher files.</strong> We can open many Microsoft Publisher (.pub) files, but very old formats (Publisher 98/2000) and some complex layouts may not convert perfectly. Please keep your originals.</p>
        </>
      ),
    },
    {
      id: 'acceptable', title: '8. Acceptable use',
      body: (
        <>
          <p>Please do not use {L.brand} to:</p>
          <ul>
            <li>create or share content that is illegal, infringes someone else's rights, or is harmful or harassing;</li>
            <li>attempt to break, overload, reverse-engineer or gain unauthorised access to the service or other people's accounts;</li>
            <li>resell or redistribute the service, or share one account between several people;</li>
            <li>abuse the trial, discount or referral programmes.</li>
          </ul>
          <p>We may remove content or suspend accounts that break these rules, and we will tell you why unless the law prevents it.</p>
        </>
      ),
    },
    {
      id: 'third', title: '9. Third-party services',
      body: (
        <>
          <p>The service relies on partners: <strong>Stripe</strong> for payments and the billing portal, <strong>Google</strong> if you choose to sign in with Google, <strong>Unsplash</strong> and <strong>Pexels</strong> for stock-photo search, and <strong>Google Fonts</strong> for the font library. Your use of those features is also subject to their terms. See the <To to="/privacy">Privacy Policy</To> for what is shared with each.</p>
        </>
      ),
    },
    {
      id: 'availability', title: '10. Availability and changes',
      body: (
        <>
          <p>We aim to keep {L.brand} available around the clock, but like any online service it may occasionally be interrupted for maintenance, updates or reasons outside our control. We regularly improve the service and may add, change or remove features; if a change materially reduces what you are paying for, you can cancel and ask for a pro-rated refund of any prepaid, unused time.</p>
          <p>Please keep your own copies of important work. The app autosaves to your account and lets you export at any time, but we recommend downloading a PDF or project export of anything you cannot afford to lose.</p>
        </>
      ),
    },
    {
      id: 'termination', title: '11. Ending the agreement',
      body: (
        <>
          <p><strong>By you:</strong> cancel your subscription as described above, and if you also want your data gone, delete your account from <strong>Profile settings ▸ Delete account</strong>. Deleting your account cancels any subscription immediately and permanently removes your publications, files, fonts and settings.</p>
          <p><strong>By us:</strong> we may suspend or close an account that seriously or repeatedly breaks these terms. If we close your account for any other reason we will give you reasonable notice and refund any prepaid, unused time.</p>
        </>
      ),
    },
    {
      id: 'liability', title: '12. Disclaimers and limits on liability',
      body: (
        <>
          <p>The service is provided "as is". We do not promise it will be error-free, uninterrupted or suitable for a particular purpose, and we are not responsible for the content you create or for print results from third-party printers.</p>
          <p>To the extent the law allows, our total liability to you for anything arising from the service in any 12-month period is limited to the amount you paid us in that period. We are not liable for indirect losses such as lost profits or lost data — but nothing in these terms excludes liability that cannot legally be excluded, such as for fraud, death or personal injury caused by negligence, or your statutory consumer rights.</p>
        </>
      ),
    },
    {
      id: 'changes', title: '13. Changes to these terms',
      body: (
        <>
          <p>We may update these terms as the service evolves. For significant changes we will email you or show a notice in the app at least 14 days in advance. Continuing to use the service after a change takes effect means you accept the new terms; if you do not, cancel before then.</p>
        </>
      ),
    },
    {
      id: 'law', title: '14. Governing law',
      body: (
        <>
          <p>These terms are governed by the laws of {L.jurisdiction}, without affecting any mandatory consumer-protection rules of the country you live in. We would much rather sort out any problem directly — please <Mail>write to us</Mail> first.</p>
        </>
      ),
    },
    {
      id: 'contact', title: '15. Contact',
      body: (
        <>
          <p>Questions about these terms, billing or your account: <Contact />{L.operatorAddress ? <> · {L.operator}, {L.operatorAddress}</> : null}.</p>
        </>
      ),
    },
  ],
};

export const PRIVACY = {
  id: 'privacy',
  title: 'Privacy Policy',
  intro: `This policy explains what information ${L.brand} collects, why, who it is shared with and how you stay in control. The short version: we keep only what the service needs, we never see your card details, we do not sell data, and you can export or delete everything yourself from Profile settings.`,
  sections: [
    {
      id: 'controller', title: '1. Who is responsible',
      body: (
        <>
          <p><strong>{L.operator}</strong>{L.operatorAddress ? ` (${L.operatorAddress})` : ''} operates {L.brand} and is the "data controller" for the information described here. Privacy questions: <Contact />.</p>
        </>
      ),
    },
    {
      id: 'collect', title: '2. What we collect',
      body: (
        <>
          <ul>
            <li><strong>Account details</strong> — your name, email address and a one-way hash of your password (we cannot read it). If you sign in with Google we receive your name, email, Google account ID and profile photo. Any avatar you upload and your chosen accent colour.</li>
            <li><strong>Your content</strong> — publications and their pages, images and fonts you upload, templates you save, folders, the Trash, your activity feed (which publication you edited and when) and any share links you create.</li>
            <li><strong>Billing status</strong> — your Stripe customer ID, which plan you are on, whether it is trialing, active or past due and the renewal date. Your card number, expiry and CVC are entered on Stripe's pages and <strong>never reach our servers</strong>.</li>
            <li><strong>Referral information</strong> — your referral code, which account referred you (if any) and the rewards earned.</li>
            <li><strong>Technical data</strong> — a session cookie so you stay signed in, and ordinary server logs (IP address, browser type, time and the pages or API calls requested) kept for security and troubleshooting.</li>
            <li><strong>In your browser only</strong> — preferences such as light/dark mode, recently used fonts and colours, open tabs and unsaved drafts are stored in your browser's local storage so the editor can pick up where you left off. They are not sent to us except as part of saving a publication.</li>
          </ul>
          <p>We do not use advertising trackers or analytics cookies.</p>
        </>
      ),
    },
    {
      id: 'use', title: '3. How we use it',
      body: (
        <>
          <ul>
            <li>To run the service: sign you in, store and open your publications, convert Publisher files, export PDFs and images, create share links.</li>
            <li>To bill you: start trials and subscriptions, apply discounts and referral credits, and keep your access in step with Stripe.</li>
            <li>To email you about your account: a one-time welcome note when you create it, password-reset links and billing notices (trial started, a reminder about three days before a free trial ends, payment failed, referral reward, subscription ended). These are transactional emails — we do not send marketing emails unless you separately opt in.</li>
            <li>To keep the service secure and working: detecting abuse, fixing faults, preventing fraud.</li>
            <li>To meet legal obligations such as tax and accounting records.</li>
          </ul>
          <p>If you are in the EU/UK, our legal bases are: performing our contract with you (most of the above), our legitimate interests (security, troubleshooting, preventing abuse), legal obligation (tax records) and your consent where we ask for it.</p>
        </>
      ),
    },
    {
      id: 'share', title: '4. Who we share it with',
      body: (
        <>
          <p>We share only what each partner needs to do its job, and we never sell personal data:</p>
          <ul>
            <li><strong>Stripe</strong> (payments) — your email and name so it can create your customer record, and the subscription you chose. Stripe's own privacy policy applies to the card details you enter on its pages.</li>
            <li><strong>Email delivery</strong> — your email address and the content of the transactional email, sent through our email provider (Resend) from our own domain, wannapublish.com.</li>
            <li><strong>Google</strong> — only if you choose "Continue with Google"; Google tells us who you are so we can sign you in.</li>
            <li><strong>Unsplash and Pexels</strong> — when you search for stock photos, your search words are sent to their APIs to fetch results.</li>
            <li><strong>Google Fonts</strong> — the editor loads font files from Google's servers, which means Google sees the request (including your IP address) like any web font.</li>
            <li><strong>Hosting and database providers</strong> that run our servers and store your data under contract with us.</li>
            <li><strong>People you share with</strong> — anyone who opens a share link you created and signs in receives a copy of that publication and can see that it came from you (your display name).</li>
          </ul>
          <p>We may also disclose information if the law requires it, or to protect the rights and safety of users or the service.</p>
        </>
      ),
    },
    {
      id: 'retention', title: '5. How long we keep it',
      body: (
        <>
          <ul>
            <li><strong>Your account and content</strong> — for as long as your account exists. A lapsed subscription does not delete anything.</li>
            <li><strong>Trash</strong> — publications you delete sit in the Trash for {L.trashDays} days (you can restore them) and are then permanently removed.</li>
            <li><strong>Account deletion</strong> — <strong>Profile settings ▸ Delete account</strong> immediately cancels any subscription and permanently deletes your publications, files, fonts, templates, folders, activity and sessions. Residual copies in backups are overwritten within about 30 days.</li>
            <li><strong>Billing records</strong> — Stripe keeps invoices and payment records for as long as tax and accounting law requires, even after you delete your account.</li>
            <li><strong>Server logs</strong> — typically 30 days.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'rights', title: '6. Your rights and controls',
      body: (
        <>
          <p>You do not need to ask us for most things — they are built in:</p>
          <ul>
            <li><strong>Access &amp; portability</strong> — <em>Profile settings ▸ Export my data</em> downloads everything (publications, fonts, templates) as a ZIP you can keep or import again.</li>
            <li><strong>Correction</strong> — change your name, email, avatar or password in <em>Profile settings</em>.</li>
            <li><strong>Deletion</strong> — <em>Profile settings ▸ Delete account</em>.</li>
            <li><strong>Billing</strong> — <em>Account ▸ Billing &amp; plan</em> opens your Stripe billing page for invoices, card and cancellation.</li>
          </ul>
          <p>You can also email <Contact /> to exercise any right under the law that applies to you (including objecting to or restricting processing, and withdrawing consent). If you are in the EU/UK you may complain to your local data-protection authority; if you are in California you have the right to know, delete and not be discriminated against for exercising your rights — we do not "sell" or "share" personal information as those terms are defined in California law.</p>
        </>
      ),
    },
    {
      id: 'security', title: '7. Security',
      body: (
        <>
          <p>All traffic is encrypted (HTTPS). Passwords are stored only as salted hashes. Sign-in uses a secure, HTTP-only session cookie; signing out or changing your password ends sessions on other devices. Card data is handled entirely by Stripe. Access to production systems is restricted and keyed. No system is perfectly secure, so please use a strong, unique password and tell us at <Contact /> if you notice anything suspicious.</p>
        </>
      ),
    },
    {
      id: 'transfers', title: '8. Where data is processed',
      body: (
        <>
          <p>Our servers and partners may be located in the United States and other countries. Where data about EU/UK residents is transferred abroad we rely on recognised safeguards such as standard contractual clauses or the partner's adequacy certification.</p>
        </>
      ),
    },
    {
      id: 'children', title: '9. Children',
      body: (
        <>
          <p>{L.brand} is not directed at children under 16 and we do not knowingly collect their data. If you believe a child has created an account, contact <Contact /> and we will remove it.</p>
        </>
      ),
    },
    {
      id: 'changes', title: '10. Changes to this policy',
      body: (
        <>
          <p>We will post any changes here and update the date at the top. For significant changes we will email you or show a notice in the app.</p>
        </>
      ),
    },
    {
      id: 'contact', title: '11. Contact',
      body: (
        <>
          <p><Contact />{L.operatorAddress ? <> · {L.operator}, {L.operatorAddress}</> : null}. Related: <To to="/terms">Terms of Service</To>.</p>
        </>
      ),
    },
  ],
};

export const LEGAL_DOCS = { terms: TERMS, privacy: PRIVACY };
